import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";
import { useVisionUIController, setOpenConfigurator } from "context";
import logo from "assets/images/bbsys.png";
import routes from "routes";
import axios from "axios";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

function Configurator() {
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } = controller;
  const navigate = useNavigate();
  const configuratorRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [regions, setRegions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMenu, setCurrentMenu] = useState("");

  // Get user role from localStorage
  const userRole = localStorage.getItem("role_id");
  const roleId = userRole ? parseInt(userRole) : null;
  const userToken = localStorage.getItem("token");
  const userId = localStorage.getItem("user_id");

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);

  const handleNavigation = (route) => {
    navigate(route);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch user sites
        const sitesResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/sites",
          {
            headers: { Authorization: userToken },
            params: { user_id: userId },
          }
        );
        setSites(sitesResponse.data || []);

        // Fetch user regions
        const regionsResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/user/regions",
          {
            headers: { Authorization: userToken },
            params: { user_id: userId },
          }
        );
        const formattedRegions = (regionsResponse.data || []).map((region) => ({
          region_id: region[0],
          name: region[1],
          description: region[2],
          created_at: region[3],
          updated_at: region[4],
        }));
        setRegions(formattedRegions);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };

    if (userId && userToken) {
      fetchUserData();
    }
  }, [userId, userToken, roleId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (configuratorRef.current && !configuratorRef.current.contains(event.target)) {
        handleCloseConfigurator();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [configuratorRef]);

  // Function to check if a route should be visible based on user role
  const shouldShowRoute = (route) => {
    // Always show these basic routes to all users
    const commonRoutes = ["Settings", "Sign Out"];
    if (commonRoutes.includes(route.name)) return true;

    // Admin has access to everything
    if (roleId === 3) return true; // Assuming 3 is Admin role_id

    // Role-specific routes
    switch (roleId) {
      case 1: // Area Manager
        return [
          "Area Dashboard",
          "Site Dashboard",
          "Data Export",
          "PPM Section List",
          "Stock Items",
          "Train Load Export",
          "Train Load Recipients",
        ].includes(route.name);
      case 2: // Site Manager
        return [
          "Site Dashboard", 
          "Data Export", 
          "PPM Section List", 
          "Stock Items",
          "Train Load Export",
          "Train Load Recipients"
        ].includes(route.name);
      case 4: // Engineer
        return ["Engineer Page", "PPM Section List"].includes(route.name);
      case 5: // National Manager
        return [
          "National Dashboard",
          "Area Dashboard",
          "Site Dashboard",
          "Data Export",
          "PPM Section List",
          "Stock Items",
          "Train Load Export",
          "Train Load Recipients",
        ].includes(route.name);
      default:
        return false;
    }
  };

  const handleMenuOpen = (event, menuType) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menuType);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMenu("");
  };

  const handleSiteSelection = (siteId) => {
    let route = "";
    switch (currentMenu) {
      case "site-dashboard":
        route = `/site-dashboard/${siteId}`;
        break;
      case "data-export":
        route = `/data-export/${siteId}`;
        break;
      case "train-load-export":
        route = `/train-load-export/${siteId}`;
        break;
      case "train-load-recipients":
        route = `/train-load-recipients/${siteId}`;
        break;
      case "ppm-section-list":
        route = `/ppm/${siteId}/section-list`;
        break;
      case "stock-items":
        route = `/stock-items/${siteId}`;
        break;
      case "engineer-page":
        route = `/engineer-page/${siteId}`;
        break;
      default:
        break;
    }

    if (route) {
      navigate(route);
      handleCloseConfigurator();
    }
    handleMenuClose();
  };

  const handleRegionSelection = (regionId) => {
    navigate(`/area-dashboard/${regionId}`);
    handleCloseConfigurator();
    handleMenuClose();
  };

  return (
    <ConfiguratorRoot ref={configuratorRef} variant="permanent" ownerState={{ openConfigurator }}>
      <VuiBox
        backgroundColor="black"
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <VuiBox component="img" src={logo} alt="Logo" width="25px" height="35px" />
        <Icon
          sx={({ typography: { size, fontWeightBold }, palette: { white } }) => ({
            fontSize: `${size.md} !important`,
            fontWeight: `${fontWeightBold} !important`,
            stroke: `${white.main} !important`,
            strokeWidth: "2px",
            cursor: "pointer",
            mt: 2,
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </VuiBox>

      <Divider light />

      <VuiBox px={3} py={2} display="flex" flexDirection="column" gap={2}>
        {routes
          .filter((route) => {
            // Skip routes that shouldn't be in the configurator
            if (
              !route.route ||
              ["Sign In", "Forgot Password", "Reset Password", "PPM", "PPM Data Export"].includes(
                route.name
              )
            ) {
              return false;
            }
            return shouldShowRoute(route);
          })
          .map(
            (route) =>
              route.type === "collapse" && (
                <div key={route.key}>
                  {route.name === "Area Dashboard" ? (
                    <>
                      <VuiButton
                        variant="text"
                        startIcon={route.icon}
                        sx={{ color: "white", justifyContent: "flex-start" }}
                        onClick={(e) => handleMenuOpen(e, "area-dashboard")}
                      >
                        {route.name}
                      </VuiButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && currentMenu === "area-dashboard"}
                        onClose={handleMenuClose}
                      >
                        {regions.map((region) => (
                          <MenuItem
                            key={region.region_id}
                            onClick={() => handleRegionSelection(region.region_id)}
                          >
                            {region.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : route.name === "National Dashboard" ? (
                    <VuiButton
                      variant="text"
                      startIcon={route.icon}
                      sx={{ color: "white", justifyContent: "flex-start" }}
                      onClick={() => handleNavigation("/national-dashboard")}
                    >
                      {route.name}
                    </VuiButton>
                  ) : [
                      "Site Dashboard",
                      "Data Export",
                      "Train Load Export",
                      "Train Load Recipients",
                      "PPM Section List",
                      "Stock Items",
                      "Engineer Page",
                    ].includes(route.name) ? (
                    <>
                      <VuiButton
                        variant="text"
                        startIcon={route.icon}
                        sx={{ color: "white", justifyContent: "flex-start" }}
                        onClick={(e) =>
                          handleMenuOpen(e, route.name.toLowerCase().replace(/\s+/g, "-"))
                        }
                      >
                        {route.name}
                      </VuiButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={
                          Boolean(anchorEl) &&
                          currentMenu === route.name.toLowerCase().replace(/\s+/g, "-")
                        }
                        onClose={handleMenuClose}
                      >
                        {sites
                          .filter((site) => {
                            // For Train Load Export and Recipients, only show sites with Train Loading in dashboard_config
                            if (currentMenu === "train-load-export" || currentMenu === "train-load-recipients") {
                              return site.dashboard_config?.includes("Train Loading");
                            }
                            return true; // Show all sites for other menus
                          })
                          .map((site) => (
                            <MenuItem
                              key={site.site_id}
                              onClick={() => handleSiteSelection(site.site_id)}
                            >
                              {site.name}
                            </MenuItem>
                          ))}
                      </Menu>
                    </>
                  ) : (
                    <VuiButton
                      variant="text"
                      startIcon={route.icon}
                      sx={{ color: "white", justifyContent: "flex-start" }}
                      onClick={() => handleNavigation(route.route)}
                    >
                      {route.name}
                    </VuiButton>
                  )}
                </div>
              )
          )}
      </VuiBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;